/**
 * useChatUnread — app-wide total of unread team-chat messages.
 *
 * Reads the chat store's `unreadCounts` map (the same field ChatPage consumes)
 * and sums it. Used by the main nav Sidebar badge and the login toast so the
 * presence of unread chat is visible WITHOUT opening the chat page.
 *
 * Returns a primitive number, so the zustand selector is render-stable.
 *
 * ⚠️ Source-of-truth caveat: this only reflects unread that the store actually
 * holds. If the chat socket / store hydrates ONLY when the chat page is open
 * (rather than app-wide via a provider), this returns 0 elsewhere and the
 * badge/toast won't light up until chat has been opened once. The fix is to
 * connect the chat socket at the app shell level (one small change in the
 * provider/App) — wire that and this hook lights up everywhere automatically.
 */
import useChatStore from "@/stores/chatStore";

export function useChatUnread(): number {
  return useChatStore((s) => {
    const counts = (s as { unreadCounts?: Record<string, number> })
      .unreadCounts;
    if (!counts) return 0;
    return Object.values(counts).reduce((sum, n) => sum + (Number(n) || 0), 0);
  });
}

export default useChatUnread;
